// src/pages/MyTeam.tsx - Premium UI with Enhanced Achievements

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, get, onValue, update } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import logo from '../assets/logo.png'
import { functions } from '../firebase';
import { 
  ArrowLeft, Users, Trophy, Shield, Zap, Crown, 
  Plus, LogIn, Eye, EyeOff, CheckCircle, XCircle,
  Loader2, WifiOff, UserPlus, UserCheck, Medal, LogOut,
  Calendar, Flag, Star, UserMinus, Gamepad2, Lock, Unlock,
  Copy, Check, ChevronRight, Mail, Phone, User, Clock, Gem, Trash2,
  Sparkles, TrendingUp, Award, Share2, MoreVertical, X,
  Target, MapPin, DollarSign, TrendingDown, Trophy as TrophyIcon,
  Music, Coffee, Zap as ZapIcon, Flame
} from 'lucide-react';

interface TeamData {
  teamId: string;
  teamName: string;
  game: 'BGMI' | 'FFMAX';
  password: string;
  status: 'complete' | 'incomplete';
  totalMatches: number;
  totalWinnings: number;
  totalFinishes: number;
  totalCD: number;
  createdAt: number;
  achievements: Record<string, {
    title: string;
    tier: string;
    date: number;
    position?: string; // '1st', '2nd', '3rd', '4th', '5th', '6th'
    points?: number;
    tournamentName?: string;
    tournamentId?: string;
    game?: 'BGMI' | 'FFMAX';
    prizeMoney?: number;
    kills?: number;
    totalTeams?: number;
    description?: string;
  }>;
  players: Record<string, {
    uid: string;
    name: string;
    userName: string;
    inGameName: string;
    inGameId: string;
    role: 'captain' | 'member';
    joinedAt: number;
  }>;
}

interface UserProfile {
  uid: string;
  name: string;
  userName: string;
  email: string;
  phone: string;
  balance: number;
  myTeamId?: string;
  BGMIID?: string;
  BGMIIGN?: string;
  FFMAXID?: string;
  FFMAXIGN?: string;
}

const MyTeam = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [myTeam, setMyTeam] = useState<TeamData | null>(null);
  const [loading, setLoading] = useState(true);
  const [offline, setOffline] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showJoinForm, setShowJoinForm] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);
  const [showTeamPassword, setShowTeamPassword] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState<any>(null);
  const [showAchievementDetail, setShowAchievementDetail] = useState(false);
  
  // Create Team Form State
  const [createGame, setCreateGame] = useState<'BGMI' | 'FFMAX'>('BGMI');
  const [teamName, setTeamName] = useState('');
  const [teamPassword, setTeamPassword] = useState('');
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [creating, setCreating] = useState(false);
  
  // Join Team Form State
  const [joinGame, setJoinGame] = useState<'BGMI' | 'FFMAX'>('BGMI');
  const [joinTeamId, setJoinTeamId] = useState('');
  const [joinTeamPassword, setJoinTeamPassword] = useState('');
  const [showJoinPassword, setShowJoinPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifiedTeam, setVerifiedTeam] = useState<any>(null);
  const [joinIGN, setJoinIGN] = useState('');
  const [joinID, setJoinID] = useState('');
  const [joining, setJoining] = useState(false);

  // Modal States
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [playerToRemove, setPlayerToRemove] = useState<any>(null);
  const [removing, setRemoving] = useState(false);

  // Get gradient color based on slot
  const getSlotGradient = (slot: string) => {
    const gradients = [
      'from-[#00e5ff] to-[#0066ff]',
      'from-purple-500 to-pink-500',
      'from-orange-500 to-red-500',
      'from-green-500 to-emerald-500',
      'from-blue-500 to-cyan-500'
    ];
    const index = parseInt(slot.replace('player', '')) - 1;
    return gradients[index] || gradients[0];
  };

  // Get position trophy icon
  const getPositionTrophy = (position: string) => {
    if (!position) return <TrophyIcon className="w-4 h-4 text-gray-400" />;
    
    const pos = position.toLowerCase();
    if (pos === '1st' || pos === '1') {
      return <TrophyIcon className="w-4 h-4 text-yellow-400" />;
    }
    if (pos === '2nd' || pos === '2') {
      return <TrophyIcon className="w-4 h-4 text-gray-300" />;
    }
    if (pos === '3rd' || pos === '3') {
      return <TrophyIcon className="w-4 h-4 text-amber-600" />;
    }
    return <Medal className="w-4 h-4 text-blue-400" />;
  };

  // Get position label
  const getPositionLabel = (position: string) => {
    if (!position) return '';
    const pos = position.toLowerCase();
    if (pos === '1st' || pos === '1') return '🥇 GOLD';
    if (pos === '2nd' || pos === '2') return '🥈 SILVER';
    if (pos === '3rd' || pos === '3') return '🥉 BRONZE';
    return `#${position}`;
  };

  // Get position color
  const getPositionColor = (position: string) => {
    if (!position) return 'text-gray-400';
    const pos = position.toLowerCase();
    if (pos === '1st' || pos === '1') return 'text-yellow-400';
    if (pos === '2nd' || pos === '2') return 'text-gray-300';
    if (pos === '3rd' || pos === '3') return 'text-amber-500';
    return 'text-blue-400';
  };

  // Get tier styles (previously working)
  const getTierStyles = (tier: string) => {
    switch(tier?.toUpperCase()) {
      case 'S':
        return {
          bg: 'from-yellow-500/20 to-orange-500/20',
          border: 'border-yellow-500/50',
          text: 'text-yellow-400',
          label: 'LEGEND',
          glow: 'shadow-yellow-500/20'
        };
      case 'T1':
        return {
          bg: 'from-purple-500/20 to-pink-500/20',
          border: 'border-purple-500/50',
          text: 'text-purple-400',
          label: 'ELITE',
          glow: 'shadow-purple-500/20'
        };
      case 'T2':
        return {
          bg: 'from-blue-500/20 to-cyan-500/20',
          border: 'border-blue-500/50',
          text: 'text-blue-400',
          label: 'PRO',
          glow: 'shadow-blue-500/20'
        };
      case 'T3':
        return {
          bg: 'from-green-500/20 to-emerald-500/20',
          border: 'border-green-500/50',
          text: 'text-green-400',
          label: 'RISING',
          glow: 'shadow-green-500/20'
        };
      default:
        return {
          bg: 'from-gray-500/20 to-gray-600/20',
          border: 'border-gray-500/50',
          text: 'text-gray-400',
          label: tier || 'UNKNOWN',
          glow: 'shadow-gray-500/20'
        };
    }
  };

  // Load user profile and team data
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate('/login');
        return;
      }
      
      setUser(currentUser);
      await loadUserData(currentUser.uid);
    });
    
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleOnline = () => setOffline(false);
    const handleOffline = () => setOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadUserData = async (userId: string) => {
    const db = getDatabase();
    const userRef = ref(db, `Accounts/${userId}`);
    
    const unsubscribe = onValue(userRef, async (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        const userProfile: UserProfile = {
          uid: userId,
          name: data.name || '',
          userName: data.userName || '',
          email: data.email || '',
          phone: data.phone || '',
          balance: data.balance || 0,
          myTeamId: data.myTeamId,
          BGMIID: data.BGMIID,
          BGMIIGN: data.BGMIIGN,
          FFMAXID: data.FFMAXID,
          FFMAXIGN: data.FFMAXIGN,
        };
        setProfile(userProfile);
        
        if (userProfile.myTeamId) {
          await loadTeamDetails(userProfile.myTeamId);
        } else {
          setMyTeam(null);
        }
      } else {
        setProfile(null);
        setMyTeam(null);
      }
      setLoading(false);
    });
    
    return () => unsubscribe();
  };

  const loadTeamDetails = async (teamId: string) => {
    try {
      const getTeamDetailsFn = httpsCallable(functions, 'getTeamDetails');
      const result = await getTeamDetailsFn({ teamId });
      const data = result.data as any;
      
      if (data && data.teamId) {
        setMyTeam({
          ...data,
          game: data.game || 'BGMI',
        });
      }
    } catch (error: any) {
      console.error('Error loading team details:', error);
      if (error.message?.includes('not-found') || error.message?.includes('permission-denied')) {
        setMyTeam(null);
        setProfile(prev => prev ? { ...prev, myTeamId: undefined } : null);
      }
    }
  };

  // Cloud Functions calls
  const handleCreateTeam = async () => {
    if (!teamName || !teamPassword) {
      alert('Please fill in all required fields');
      return;
    }
    
    if (teamPassword.length < 4) {
      alert('Password must be at least 4 characters');
      return;
    }
    
    setCreating(true);
    try {
      const createTeamFn = httpsCallable(functions, 'createTeam');
      const result = await createTeamFn({
        teamName: teamName.trim(),
        password: teamPassword,
        game: createGame
      });
      
      const data = result.data as any;
      
      if (data.success) {
        alert(`Team created successfully! Team ID: ${data.teamId}`);
        setShowCreateForm(false);
        resetCreateForm();
        if (user) {
          await loadUserData(user.uid);
        }
      }
    } catch (error: any) {
      console.error('Error creating team:', error);
      alert(error.message || 'Failed to create team. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleVerifyTeam = async () => {
    if (!joinTeamId || !joinTeamPassword) {
      alert('Please enter Team ID and Password');
      return;
    }
    
    if (joinTeamId.length !== 6) {
      alert('Team ID must be 6 digits');
      return;
    }
    
    setVerifying(true);
    try {
      const verifyTeamFn = httpsCallable(functions, 'verifyTeam');
      const result = await verifyTeamFn({
        teamId: joinTeamId,
        game: joinGame,
        password: joinTeamPassword
      });
      
      const data = result.data as any;
      
      if (data.success) {
        setVerifiedTeam(data.teamData);
        
        const hasIGN = joinGame === 'BGMI' ? profile?.BGMIIGN : profile?.FFMAXIGN;
        const hasID = joinGame === 'BGMI' ? profile?.BGMIID : profile?.FFMAXID;
        
        if (hasIGN && hasID) {
          setJoinIGN(hasIGN);
          setJoinID(hasID);
        }
      }
    } catch (error: any) {
      console.error('Error verifying team:', error);
      alert(error.message || 'Failed to verify team');
    } finally {
      setVerifying(false);
    }
  };

  const handleJoinTeam = async () => {
    if (!joinIGN || !joinID) {
      alert('Please enter your IGN and ID');
      return;
    }
    
    setJoining(true);
    try {
      const joinTeamFn = httpsCallable(functions, 'joinTeam');
      const result = await joinTeamFn({
        teamId: joinTeamId,
        password: joinTeamPassword,
        game: joinGame,
        ign: joinIGN.trim(),
        id: joinID.trim()
      });
      
      const data = result.data as any;
      
      if (data.success) {
        alert('Successfully joined the team!');
        setShowJoinForm(false);
        resetJoinForm();
        if (user) {
          await loadUserData(user.uid);
        }
      }
    } catch (error: any) {
      console.error('Error joining team:', error);
      alert(error.message || 'Failed to join team');
    } finally {
      setJoining(false);
    }
  };

  const handleLeaveTeam = async () => {
    if (!myTeam || !profile?.myTeamId) return;
    
    const isCaptain = myTeam.players.player1?.uid === user?.uid;
    const playerCount = Object.keys(myTeam.players || {}).length;
    
    if (isCaptain && playerCount > 1) {
      alert('As captain, you cannot leave the team with other members. Please transfer captaincy first or delete the team.');
      setShowLeaveModal(false);
      return;
    }
    
    setLoading(true);
    try {
      const leaveTeamFn = httpsCallable(functions, 'leaveTeam');
      const result = await leaveTeamFn({});
      
      const data = result.data as any;
      
      if (data.success) {
        alert('You have left the team successfully');
        if (user) {
          await loadUserData(user.uid);
        }
      }
    } catch (error: any) {
      console.error('Error leaving team:', error);
      alert(error.message || 'Failed to leave team');
    } finally {
      setLoading(false);
      setShowLeaveModal(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!myTeam || !profile?.myTeamId) return;
    
    const isCaptain = myTeam.players.player1?.uid === user?.uid;
    
    if (!isCaptain) {
      alert('Only team captain can delete the team');
      setShowDeleteModal(false);
      return;
    }
    
    setLoading(true);
    try {
      const deleteTeamFn = httpsCallable(functions, 'deleteTeam');
      const result = await deleteTeamFn({});
      
      const data = result.data as any;
      
      if (data.success) {
        alert('Team deleted successfully');
        if (user) {
          await loadUserData(user.uid);
        }
      }
    } catch (error: any) {
      console.error('Error deleting team:', error);
      alert(error.message || 'Failed to delete team');
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleRemovePlayer = async () => {
    if (!playerToRemove || !myTeam || !profile?.myTeamId) return;
    
    setRemoving(true);
    try {
      const removePlayerFn = httpsCallable(functions, 'removePlayer');
      const result = await removePlayerFn({
        teamId: myTeam.teamId,
        playerId: playerToRemove.uid
      });
      
      const data = result.data as any;
      
      if (data.success) {
        alert(`${playerToRemove.name || playerToRemove.userName} has been removed from the team`);
        setShowRemoveModal(false);
        setPlayerToRemove(null);
        
        if (user) {
          await loadUserData(user.uid);
        }
      }
    } catch (error: any) {
      console.error('Error removing player:', error);
      alert(error.message || 'Failed to remove player');
    } finally {
      setRemoving(false);
    }
  };

  const resetCreateForm = () => {
    setCreateGame('BGMI');
    setTeamName('');
    setTeamPassword('');
    setShowCreatePassword(false);
  };

  const resetJoinForm = () => {
    setJoinGame('BGMI');
    setJoinTeamId('');
    setJoinTeamPassword('');
    setVerifiedTeam(null);
    setJoinIGN('');
    setJoinID('');
    setShowJoinPassword(false);
  };

  const copyToClipboard = (text: string, type: 'id' | 'password') => {
    navigator.clipboard.writeText(text);
    if (type === 'id') {
      setCopiedId(true);
      setTimeout(() => setCopiedId(false), 2000);
    } else {
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    }
  };

  // Achievement Detail Modal
  const AchievementDetailModal = ({ achievement, onClose }: any) => {
    const tierStyles = getTierStyles(achievement.tier);
    const positionColor = getPositionColor(achievement.position);
    const positionLabel = getPositionLabel(achievement.position);
    const positionTrophy = getPositionTrophy(achievement.position);
    
    return (
      <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
        <div className="bg-[#1a1a2e] rounded-2xl p-6 max-w-md w-full border border-white/10 shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tierStyles.bg} border ${tierStyles.border} flex items-center justify-center`}>
                {positionTrophy}
              </div>
              <div>
                <h3 className="text-white font-rajdhani font-bold text-lg">{achievement.title}</h3>
                {achievement.position && (
                  <span className={`text-sm font-rajdhani font-bold ${positionColor}`}>
                    {positionLabel}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="space-y-3 mb-4">
            {/* Position */}
            {achievement.position && (
              <div className="flex items-center justify-between p-2 bg-white/5 rounded-xl">
                <span className="text-gray-400 text-sm">Position</span>
                <span className={`font-rajdhani font-bold ${positionColor}`}>
                  {positionLabel}
                </span>
              </div>
            )}
            
            {/* Points */}
            {achievement.points !== undefined && achievement.points !== null && (
              <div className="flex items-center justify-between p-2 bg-white/5 rounded-xl">
                <span className="text-gray-400 text-sm">Points</span>
                <span className="text-purple-400 font-rajdhani font-bold">{achievement.points}</span>
              </div>
            )}
            
            {/* Tier */}
            <div className="flex items-center justify-between p-2 bg-white/5 rounded-xl">
              <span className="text-gray-400 text-sm">Tier</span>
              <span className={`font-rajdhani font-bold ${tierStyles.text}`}>
                {tierStyles.label}
              </span>
            </div>
            
            {/* Tournament Name */}
            {achievement.tournamentName && (
              <div className="flex items-center justify-between p-2 bg-white/5 rounded-xl">
                <span className="text-gray-400 text-sm">Tournament</span>
                <span className="text-white font-rajdhani font-bold text-sm">{achievement.tournamentName}</span>
              </div>
            )}
            
            {/* Game */}
            {achievement.game && (
              <div className="flex items-center justify-between p-2 bg-white/5 rounded-xl">
                <span className="text-gray-400 text-sm">Game</span>
                <span className={`px-2 py-0.5 rounded-lg text-xs font-rajdhani font-bold ${
                  achievement.game === 'BGMI' 
                    ? 'bg-purple-500/20 text-purple-400' 
                    : 'bg-orange-500/20 text-orange-400'
                }`}>
                  {achievement.game}
                </span>
              </div>
            )}
            
            {/* Prize Money */}
            {achievement.prizeMoney && (
              <div className="flex items-center justify-between p-2 bg-white/5 rounded-xl">
                <span className="text-gray-400 text-sm">Prize Money</span>
                <span className="text-yellow-400 font-rajdhani font-bold">₹{achievement.prizeMoney.toLocaleString()}</span>
              </div>
            )}
            
            {/* Kills */}
            {achievement.kills !== undefined && achievement.kills !== null && (
              <div className="flex items-center justify-between p-2 bg-white/5 rounded-xl">
                <span className="text-gray-400 text-sm">Kills</span>
                <span className="text-red-400 font-rajdhani font-bold">{achievement.kills}</span>
              </div>
            )}
            
            {/* Total Teams */}
            {achievement.totalTeams && (
              <div className="flex items-center justify-between p-2 bg-white/5 rounded-xl">
                <span className="text-gray-400 text-sm">Total Teams</span>
                <span className="text-white font-rajdhani font-bold">{achievement.totalTeams}</span>
              </div>
            )}
            
            {/* Date */}
            {achievement.date && (
              <div className="flex items-center justify-between p-2 bg-white/5 rounded-xl">
                <span className="text-gray-400 text-sm">Date</span>
                <span className="text-gray-300 text-sm">
                  {new Date(achievement.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </span>
              </div>
            )}
            
            {/* Description */}
            {achievement.description && (
              <div className="p-3 bg-white/5 rounded-xl">
                <p className="text-gray-400 text-sm mb-1">Description</p>
                <p className="text-gray-300 text-sm">{achievement.description}</p>
              </div>
            )}
          </div>
          
          <button
            onClick={onClose}
            className="w-full px-4 py-3 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl text-white font-rajdhani font-bold hover:shadow-lg hover:shadow-purple-500/25 transition"
          >
            Close
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A0A0F] to-[#0F0F1A] flex items-center justify-center overflow-hidden">
        <div className="absolute w-60 h-60 bg-purple-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute w-72 h-72 bg-blue-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ top: '20%', left: '10%' }} />
        <div className="absolute w-56 h-56 bg-pink-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ bottom: '20%', right: '10%' }} />
        
        <div className="relative flex flex-col items-center">
          <div className="relative w-28 h-28">
            <div className="absolute inset-0 rounded-full">
              <svg className="w-full h-full animate-spin-slow" viewBox="0 0 100 100">
                <defs>
                  <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#A855F7" />
                    <stop offset="50%" stopColor="#3B82F6" />
                    <stop offset="100%" stopColor="#EC4899" />
                  </linearGradient>
                </defs>
                <circle
                  cx="50"
                  cy="50"
                  r="44"
                  fill="none"
                  stroke="url(#ringGradient)"
                  strokeWidth="3"
                  strokeDasharray="200"
                  strokeDashoffset="50"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-blue-500/20 to-pink-500/20 blur-xl rounded-full animate-pulse" />
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-pink-500/10 rounded-full blur-md animate-pulse-slow" />
            
            <div className="absolute inset-0 flex items-center justify-center">
              <img
                src={logo}
                alt="ArenaX"
                className="w-20 h-20 object-contain animate-float"
              />
            </div>
          </div>

          <div className="mt-5 text-center">
            <p className="text-transparent bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text font-rajdhani font-bold text-base tracking-widest animate-pulse">
              LOADING ARENA...
            </p>
            <div className="flex items-center justify-center gap-1.5 mt-2">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-500 animate-bounce" style={{ animationDelay: '0s' }} />
              <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0.2s' }} />
              <div className="w-1.5 h-1.5 rounded-full bg-pink-500 animate-bounce" style={{ animationDelay: '0.4s' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (offline) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
            <WifiOff className="w-10 h-10 text-yellow-500" />
          </div>
          <h2 className="text-white font-rajdhani font-bold text-2xl mb-2">Connection Lost</h2>
          <p className="text-gray-400 mb-6">Please check your internet connection</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-to-r from-[#00e5ff] to-[#0066ff] rounded-xl text-white font-rajdhani font-bold hover:shadow-lg hover:shadow-[#00e5ff]/20 transition-all"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0F]">
      {/* Premium Header */}
      <div className="fixed top-0 w-full bg-[#0A0A0F]/95 backdrop-blur-xl border-b border-white/5 z-50">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(-1)} 
              className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all hover:scale-105"
            >
              <ArrowLeft className="w-5 h-5 text-white" />
            </button>
            <div>
              <h1 className="text-white font-rajdhani font-bold text-lg tracking-wider">MY TEAM</h1>
              {myTeam && (
                <p className="text-[10px] text-gray-500 font-mono">{myTeam.teamId}</p>
              )}
            </div>
          </div>
          {myTeam && (
            <button className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-all hover:scale-105">
              <Share2 className="w-5 h-5 text-gray-400" />
            </button>
          )}
        </div>
      </div>

      <div className="pt-20 pb-8 px-4 max-w-4xl mx-auto">
        {profile?.myTeamId && myTeam ? (
          <div>
            {/* Premium Team Header Card */}
            <div className="relative overflow-hidden rounded-2xl p-6 mb-6 border border-white/10 bg-gradient-to-br from-[#1a1a2e] to-[#0a0a1a]">
              {/* Background Glow Effects */}
              <div className="absolute -top-20 -right-20 w-60 h-60 bg-[#00e5ff]/10 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-purple-500/10 rounded-full blur-3xl"></div>
              
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00e5ff] to-[#0066ff] flex items-center justify-center text-3xl shadow-lg shadow-[#00e5ff]/20">
                      {myTeam.game === 'BGMI' ? '🎮' : '🔥'}
                    </div>
                    <div>
                      <h2 className="text-white font-rajdhani font-bold text-2xl tracking-wide">{myTeam.teamName}</h2>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <Lock className="w-3 h-3" />
                          <span>ID: {myTeam.teamId}</span>
                          <button
                            onClick={() => copyToClipboard(myTeam.teamId, 'id')}
                            className="text-[#00e5ff] hover:text-[#00e5ff]/80 transition"
                          >
                            {copiedId ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </button>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <span>Password:</span>
                          <span className="text-white font-mono">
                            {showTeamPassword ? myTeam.password : '••••••'}
                          </span>
                          <button
                            onClick={() => setShowTeamPassword(!showTeamPassword)}
                            className="text-[#00e5ff] hover:text-[#00e5ff]/80 transition"
                          >
                            {showTeamPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                          </button>
                          <button
                            onClick={() => copyToClipboard(myTeam.password, 'password')}
                            className="text-[#00e5ff] hover:text-[#00e5ff]/80 transition"
                          >
                            {copiedPassword ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-rajdhani font-bold border ${
                    myTeam.status === 'complete'
                      ? 'bg-green-500/20 text-green-400 border-green-500/30'
                      : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                  }`}>
                    {myTeam.status === 'complete' ? 'COMPLETE' : 'INCOMPLETE'}
                  </div>
                </div>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-4 gap-3 mt-4">
                  <div className="bg-white/5 rounded-xl p-3 text-center backdrop-blur-sm border border-white/5">
                    <div className="text-yellow-500 font-rajdhani font-bold text-lg">{myTeam.totalMatches || 0}</div>
                    <div className="text-gray-500 text-[10px] uppercase tracking-wider">Matches</div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 text-center backdrop-blur-sm border border-white/5">
                    <div className="text-green-500 font-rajdhani font-bold text-lg">₹{(myTeam.totalWinnings || 0).toLocaleString()}</div>
                    <div className="text-gray-500 text-[10px] uppercase tracking-wider">Won</div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 text-center backdrop-blur-sm border border-white/5">
                    <div className="text-purple-500 font-rajdhani font-bold text-lg">{myTeam.totalFinishes || 0}</div>
                    <div className="text-gray-500 text-[10px] uppercase tracking-wider">Finishes</div>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 text-center backdrop-blur-sm border border-white/5">
                    <div className="text-blue-500 font-rajdhani font-bold text-lg">{myTeam.totalCD || 0}</div>
                    <div className="text-gray-500 text-[10px] uppercase tracking-wider">CD</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Premium Players Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-rajdhani font-bold flex items-center gap-2 text-lg">
                  <div className="w-6 h-6 rounded-lg bg-[#00e5ff]/20 flex items-center justify-center">
                    <Users className="w-3.5 h-3.5 text-[#00e5ff]" />
                  </div>
                  Squad ({Object.keys(myTeam.players || {}).length}/5)
                </h3>
              </div>

              {/* Player Cards - Premium Design */}
              <div className="space-y-3">
                {myTeam.players && Object.entries(myTeam.players).map(([slot, player]: [string, any]) => {
                  const isCaptain = slot === 'player1';
                  const isCurrentUser = player.uid === user?.uid;
                  const isTeamCaptain = myTeam.players.player1?.uid === user?.uid;
                  const playerNumber = slot.replace('player', '');
                  const gradient = getSlotGradient(slot);
                  const canRemove = isTeamCaptain && !isCaptain && !isCurrentUser;

                  return (
                    <div 
                      key={slot} 
                      className={`group relative overflow-hidden rounded-xl p-4 border transition-all duration-300 ${
                        isCurrentUser 
                          ? 'bg-gradient-to-r from-[#00e5ff]/10 to-[#0066ff]/10 border-[#00e5ff]/30 hover:border-[#00e5ff]/50' 
                          : 'bg-[#1a1a2e] border-white/5 hover:border-white/20'
                      } hover:scale-[1.02] hover:shadow-lg hover:shadow-[#00e5ff]/5`}
                    >
                      <div className="relative z-10 flex items-center gap-4">
                        {/* Premium Avatar with Number and Captain Crown */}
                        <div className="relative flex-shrink-0">
                          <div className={`w-14 h-14 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center shadow-lg`}>
                            <span className="text-white font-rajdhani font-bold text-xl">
                              {playerNumber}
                            </span>
                          </div>
                          {/* Captain Crown Indicator */}
                          {isCaptain && (
                            <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center border-2 border-[#1a1a2e] shadow-lg shadow-yellow-500/30">
                              <Crown className="w-3.5 h-3.5 text-white" />
                            </div>
                          )}
                          {isCurrentUser && (
                            <div className="absolute -bottom-1 -right-1 px-2 py-0.5 bg-[#00e5ff] rounded-full text-[8px] font-bold text-black shadow-lg shadow-[#00e5ff]/30">
                              YOU
                            </div>
                          )}
                        </div>

                        {/* Player Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="text-white font-rajdhani font-bold text-base truncate">
                              {player.inGameName || player.name}
                            </h4>
                            {isCurrentUser && (
                              <span className="text-[10px] px-2 py-0.5 rounded-full font-rajdhani font-bold bg-[#00e5ff]/20 text-[#00e5ff] border border-[#00e5ff]/30">
                                YOU
                              </span>
                            )}
                          </div>
                          <p className="text-gray-400 text-sm">@{player.userName}</p>
                          {player.inGameName && player.inGameId && (
                            <div className="flex items-center gap-3 mt-1 text-xs">
                              <span className="text-gray-500">IGN: <span className="text-[#00e5ff] font-medium">{player.inGameName}</span></span>
                              <span className="text-gray-600">|</span>
                              <span className="text-gray-500">ID: <span className="text-white font-mono">{player.inGameId}</span></span>
                            </div>
                          )}
                        </div>

                        {/* Remove Player Button - Only for Captain (not for self or captain) */}
                        {canRemove && (
                          <button
                            onClick={() => {
                              setPlayerToRemove(player);
                              setShowRemoveModal(true);
                            }}
                            className="flex-shrink-0 p-2 bg-red-500/10 hover:bg-red-500/20 rounded-lg text-red-400 hover:text-red-300 transition-all border border-red-500/20 hover:border-red-500/40"
                            title="Remove player from team"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Achievements Section - Enhanced */}
            <div className="mb-6">
              <h3 className="text-white font-rajdhani font-bold mb-4 flex items-center gap-2 text-lg">
                <div className="w-6 h-6 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                  <Trophy className="w-3.5 h-3.5 text-yellow-500" />
                </div>
                Achievements ({Object.keys(myTeam.achievements || {}).length})
              </h3>
              
              {myTeam.achievements && Object.keys(myTeam.achievements).length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(myTeam.achievements).map(([key, achievement]: [string, any]) => {
                    const tierStyles = getTierStyles(achievement.tier);
                    const positionColor = getPositionColor(achievement.position);
                    const positionLabel = getPositionLabel(achievement.position);
                    const positionTrophy = getPositionTrophy(achievement.position);
                    
                    return (
                      <div 
                        key={key} 
                        onClick={() => {
                          setSelectedAchievement(achievement);
                          setShowAchievementDetail(true);
                        }}
                        className={`p-4 rounded-xl border ${tierStyles.border} bg-gradient-to-br ${tierStyles.bg} hover:scale-105 transition-all duration-300 cursor-pointer shadow-lg ${tierStyles.glow}`}
                      >
                        <div className="flex items-start gap-3">
                          {/* Tier Icon */}
                          <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${tierStyles.bg} border ${tierStyles.border} flex items-center justify-center flex-shrink-0`}>
                            {positionTrophy}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-white font-rajdhani font-bold text-sm truncate">{achievement.title}</p>
                              {/* Position Trophy Icon after title */}
                              <span className={`flex-shrink-0 ${positionColor}`}>
                                {positionTrophy}
                              </span>
                            </div>
                            
                            {/* Position Label */}
                            {achievement.position && (
                              <span className={`text-xs font-rajdhani font-bold ${positionColor}`}>
                                {positionLabel}
                              </span>
                            )}
                            
                            {/* Points */}
                            {achievement.points !== undefined && achievement.points !== null && (
                              <span className="text-xs text-purple-400 font-rajdhani font-bold ml-2">
                                • {achievement.points} pts
                              </span>
                            )}
                            
                            {/* Date */}
                            {achievement.date && (
                              <p className="text-gray-400 text-[10px] flex items-center gap-1 mt-0.5">
                                <Calendar className="w-3 h-3" />
                                {new Date(achievement.date).toLocaleDateString('en-US', {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </p>
                            )}
                          </div>
                          
                          {/* Tier Label */}
                          <div className={`px-2 py-0.5 rounded-full text-[10px] font-rajdhani font-bold ${tierStyles.text} border ${tierStyles.border} flex-shrink-0`}>
                            {tierStyles.label}
                          </div>
                        </div>
                        
                        {/* Additional details - Larger and more visible */}
                        {(achievement.prizeMoney || achievement.kills !== undefined) && (
                          <div className="flex items-center gap-4 mt-3 pt-2 border-t border-white/10">
                            {achievement.prizeMoney && (
                              <span className="text-sm text-yellow-400 font-rajdhani font-bold flex items-center gap-1.5">
                                <DollarSign className="w-4 h-4" />
                                ₹{achievement.prizeMoney.toLocaleString()}
                              </span>
                            )}
                            {achievement.kills !== undefined && achievement.kills !== null && (
                              <span className="text-sm text-red-400 font-rajdhani font-bold flex items-center gap-1.5">
                                <Target className="w-4 h-4" />
                                {achievement.kills} kills
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="bg-[#1a1a2e] rounded-xl p-8 text-center border border-white/5">
                  <div className="w-16 h-16 bg-gray-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Trophy className="w-8 h-8 text-gray-500" />
                  </div>
                  <h4 className="text-white font-rajdhani font-bold text-lg mb-2">No Achievements Yet</h4>
                  <p className="text-gray-400 text-sm mb-3">Your team hasn't won any tournaments yet</p>
                  <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                    <Sparkles className="w-3 h-3 text-yellow-500" />
                    <span>Participate in tournaments to earn achievements</span>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            {(() => {
              const playerCount = Object.keys(myTeam.players || {}).length;
              const isCaptain = myTeam.players.player1?.uid === user?.uid;
              const isOnlyMember = playerCount === 1 && isCaptain;
              
              return (
                <div className="flex gap-3">
                  {isOnlyMember ? (
                    <button
                      onClick={() => setShowDeleteModal(true)}
                      className="flex-1 bg-gradient-to-r from-red-500 to-red-600 py-3.5 rounded-xl text-white font-rajdhani font-bold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-red-500/20 transition-all hover:scale-[1.02]"
                    >
                      <Trash2 className="w-5 h-5" />
                      Delete Team
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowLeaveModal(true)}
                      className="flex-1 bg-gradient-to-r from-red-500 to-red-600 py-3.5 rounded-xl text-white font-rajdhani font-bold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-red-500/20 transition-all hover:scale-[1.02]"
                    >
                      <LogOut className="w-5 h-5" />
                      Leave Team
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Achievement Detail Modal */}
            {showAchievementDetail && selectedAchievement && (
              <AchievementDetailModal
                achievement={selectedAchievement}
                onClose={() => {
                  setShowAchievementDetail(false);
                  setSelectedAchievement(null);
                }}
              />
            )}

            {/* Remove Player Modal */}
            {showRemoveModal && playerToRemove && (
              <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                <div className="bg-[#1a1a2e] rounded-2xl p-6 max-w-md w-full border border-white/10 shadow-2xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                      <UserMinus className="w-6 h-6 text-red-400" />
                    </div>
                    <h3 className="text-white font-rajdhani font-bold text-xl">Remove Player?</h3>
                  </div>
                  
                  <p className="text-gray-300 mb-2">
                    Are you sure you want to remove <span className="text-[#00e5ff] font-bold">{playerToRemove.name || playerToRemove.userName}</span> from <span className="text-[#00e5ff] font-bold">{myTeam?.teamName}</span>?
                  </p>
                  <p className="text-gray-500 text-sm mb-6">
                    This action cannot be undone. The player will lose access to the team.
                  </p>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => {
                        setShowRemoveModal(false);
                        setPlayerToRemove(null);
                      }}
                      className="flex-1 px-4 py-2.5 bg-white/5 rounded-xl text-gray-300 font-rajdhani font-bold hover:bg-white/10 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRemovePlayer}
                      disabled={removing}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-500 to-red-600 rounded-xl text-white font-rajdhani font-bold hover:shadow-lg hover:shadow-red-500/20 transition disabled:opacity-50"
                    >
                      {removing ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Yes, Remove'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Leave Team Modal */}
            {showLeaveModal && (
              <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                <div className="bg-[#1a1a2e] rounded-2xl p-6 max-w-md w-full border border-white/10 shadow-2xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                      <LogOut className="w-6 h-6 text-red-400" />
                    </div>
                    <h3 className="text-white font-rajdhani font-bold text-xl">Leave Team?</h3>
                  </div>
                  
                  <p className="text-gray-300 mb-2">
                    Are you sure you want to leave <span className="text-[#00e5ff] font-bold">{myTeam?.teamName}</span>?
                  </p>
                  <p className="text-gray-500 text-sm mb-6">
                    You will lose access to team features and your stats will be removed.
                  </p>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowLeaveModal(false)}
                      className="flex-1 px-4 py-2.5 bg-white/5 rounded-xl text-gray-300 font-rajdhani font-bold hover:bg-white/10 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleLeaveTeam}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-500 to-red-600 rounded-xl text-white font-rajdhani font-bold hover:shadow-lg hover:shadow-red-500/20 transition"
                    >
                      Yes, Leave
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Team Modal */}
            {showDeleteModal && (
              <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
                <div className="bg-[#1a1a2e] rounded-2xl p-6 max-w-md w-full border border-white/10 shadow-2xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
                      <Trash2 className="w-6 h-6 text-red-400" />
                    </div>
                    <h3 className="text-white font-rajdhani font-bold text-xl">Delete Team?</h3>
                  </div>
                  
                  <p className="text-gray-300 mb-2">
                    Are you sure you want to delete <span className="text-[#00e5ff] font-bold">{myTeam?.teamName}</span>?
                  </p>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 mb-4">
                    <p className="text-red-400 text-sm font-rajdhani font-bold flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      WARNING
                    </p>
                    <p className="text-red-300 text-xs">
                      This action cannot be undone. All team data will be permanently deleted.
                    </p>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowDeleteModal(false)}
                      className="flex-1 px-4 py-2.5 bg-white/5 rounded-xl text-gray-300 font-rajdhani font-bold hover:bg-white/10 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteTeam}
                      className="flex-1 px-4 py-2.5 bg-gradient-to-r from-red-500 to-red-600 rounded-xl text-white font-rajdhani font-bold hover:shadow-lg hover:shadow-red-500/20 transition"
                    >
                      Yes, Delete
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          // No Team - Premium Empty State
          <div>
            {!showCreateForm && !showJoinForm && (
              <div className="text-center py-16">
                <div className="relative w-32 h-32 mx-auto mb-6">
                  <div className="absolute inset-0 bg-gradient-to-r from-[#00e5ff] to-[#0066ff] rounded-full opacity-20 blur-2xl animate-pulse"></div>
                  <div className="relative w-32 h-32 bg-[#1a1a2e] rounded-full flex items-center justify-center border-2 border-white/10">
                    <Users className="w-16 h-16 text-gray-500" />
                  </div>
                </div>
                <h2 className="text-white font-rajdhani font-bold text-2xl mb-2">No Team Yet</h2>
                <p className="text-gray-400 mb-8 max-w-sm mx-auto">Create your own esports team or join an existing one to start competing</p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
                  <button
                    onClick={() => setShowCreateForm(true)}
                    className="flex-1 px-6 py-3.5 bg-gradient-to-r from-[#00e5ff] to-[#0066ff] rounded-xl text-white font-rajdhani font-bold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-[#00e5ff]/20 transition-all hover:scale-[1.02]"
                  >
                    <Plus className="w-5 h-5" />
                    Create Team
                  </button>
                  <button
                    onClick={() => setShowJoinForm(true)}
                    className="flex-1 px-6 py-3.5 bg-white/5 border border-white/10 rounded-xl text-white font-rajdhani font-bold flex items-center justify-center gap-2 hover:bg-white/10 transition-all hover:scale-[1.02]"
                  >
                    <LogIn className="w-5 h-5" />
                    Join Team
                  </button>
                </div>
              </div>
            )}

            {/* Create Team Form - Premium */}
            {showCreateForm && (
              <div className="bg-[#1a1a2e] rounded-2xl p-6 border border-white/10 shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-[#00e5ff] to-[#0066ff] flex items-center justify-center">
                    <Plus className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-white font-rajdhani font-bold text-xl">Create New Team</h2>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-gray-300 text-sm font-rajdhani font-bold mb-2">Select Game</label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => setCreateGame('BGMI')}
                        className={`py-2.5 rounded-xl font-rajdhani font-bold transition-all ${
                          createGame === 'BGMI'
                            ? 'bg-gradient-to-r from-[#00e5ff] to-[#0066ff] text-white shadow-lg shadow-[#00e5ff]/20'
                            : 'bg-white/5 text-gray-400 hover:bg-white/10'
                        }`}
                      >
                        🎮 BGMI
                      </button>
                      <button
                        onClick={() => setCreateGame('FFMAX')}
                        className={`py-2.5 rounded-xl font-rajdhani font-bold transition-all ${
                          createGame === 'FFMAX'
                            ? 'bg-gradient-to-r from-[#00e5ff] to-[#0066ff] text-white shadow-lg shadow-[#00e5ff]/20'
                            : 'bg-white/5 text-gray-400 hover:bg-white/10'
                        }`}
                      >
                        🔥 FREE FIRE MAX
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-300 text-sm font-rajdhani font-bold mb-2">Team Name</label>
                    <input
                      type="text"
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#00e5ff]/50 transition"
                      placeholder="Enter team name (2-30 characters)"
                      maxLength={30}
                    />
                  </div>

                  <div>
                    <label className="block text-gray-300 text-sm font-rajdhani font-bold mb-2">Team Password</label>
                    <div className="relative">
                      <input
                        type={showCreatePassword ? 'text' : 'password'}
                        value={teamPassword}
                        onChange={(e) => setTeamPassword(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#00e5ff]/50 transition pr-12"
                        placeholder="Set team password (min 4 characters)"
                        minLength={4}
                      />
                      <button
                        onClick={() => setShowCreatePassword(!showCreatePassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition"
                      >
                        {showCreatePassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                    <div className="flex items-center gap-2 mb-2">
                      <Crown className="w-4 h-4 text-yellow-500" />
                      <span className="text-white font-rajdhani font-bold">Player 1 (Captain)</span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p className="text-gray-400">@{profile?.userName}</p>
                      <p className="text-[#00e5ff]">
                        IGN: {createGame === 'BGMI' ? profile?.BGMIIGN || 'Not set' : profile?.FFMAXIGN || 'Not set'}
                      </p>
                      <p className="text-gray-400">
                        ID: {createGame === 'BGMI' ? profile?.BGMIID || 'Not set' : profile?.FFMAXID || 'Not set'}
                      </p>
                    </div>
                  </div>

                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3">
                    <p className="text-yellow-400 text-xs flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Share your Team ID and Password with friends to invite them
                    </p>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => {
                        setShowCreateForm(false);
                        resetCreateForm();
                      }}
                      className="flex-1 px-4 py-3 bg-white/5 rounded-xl text-gray-400 font-rajdhani font-bold hover:bg-white/10 transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreateTeam}
                      disabled={creating || !teamName || !teamPassword || teamPassword.length < 4}
                      className="flex-1 px-4 py-3 bg-gradient-to-r from-[#00e5ff] to-[#0066ff] rounded-xl text-white font-rajdhani font-bold disabled:opacity-50 hover:shadow-lg hover:shadow-[#00e5ff]/20 transition"
                    >
                      {creating ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Create Team'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Join Team Form - Premium */}
            {showJoinForm && (
              <div className="bg-[#1a1a2e] rounded-2xl p-6 border border-white/10 shadow-xl">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                    <LogIn className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-white font-rajdhani font-bold text-xl">Join Team</h2>
                </div>
                
                {!verifiedTeam ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-gray-300 text-sm font-rajdhani font-bold mb-2">Select Game</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          onClick={() => setJoinGame('BGMI')}
                          className={`py-2.5 rounded-xl font-rajdhani font-bold transition-all ${
                            joinGame === 'BGMI'
                              ? 'bg-gradient-to-r from-[#00e5ff] to-[#0066ff] text-white shadow-lg shadow-[#00e5ff]/20'
                              : 'bg-white/5 text-gray-400 hover:bg-white/10'
                          }`}
                        >
                          🎮 BGMI
                        </button>
                        <button
                          onClick={() => setJoinGame('FFMAX')}
                          className={`py-2.5 rounded-xl font-rajdhani font-bold transition-all ${
                            joinGame === 'FFMAX'
                              ? 'bg-gradient-to-r from-[#00e5ff] to-[#0066ff] text-white shadow-lg shadow-[#00e5ff]/20'
                              : 'bg-white/5 text-gray-400 hover:bg-white/10'
                          }`}
                        >
                          🔥 FREE FIRE MAX
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-300 text-sm font-rajdhani font-bold mb-2">Team ID</label>
                      <input
                        type="text"
                        value={joinTeamId}
                        onChange={(e) => setJoinTeamId(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-center text-xl font-mono tracking-widest placeholder-gray-500 focus:outline-none focus:border-[#00e5ff]/50 transition"
                        placeholder="Enter 6-digit team ID"
                        maxLength={6}
                      />
                    </div>

                    <div>
                      <label className="block text-gray-300 text-sm font-rajdhani font-bold mb-2">Team Password</label>
                      <div className="relative">
                        <input
                          type={showJoinPassword ? 'text' : 'password'}
                          value={joinTeamPassword}
                          onChange={(e) => setJoinTeamPassword(e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-center placeholder-gray-500 focus:outline-none focus:border-[#00e5ff]/50 transition pr-12"
                          placeholder="Enter team password"
                        />
                        <button
                          onClick={() => setShowJoinPassword(!showJoinPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition"
                        >
                          {showJoinPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={() => {
                          setShowJoinForm(false);
                          resetJoinForm();
                        }}
                        className="flex-1 px-4 py-3 bg-white/5 rounded-xl text-gray-400 font-rajdhani font-bold hover:bg-white/10 transition"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleVerifyTeam}
                        disabled={verifying || !joinTeamId || !joinTeamPassword || joinTeamId.length !== 6}
                        className="flex-1 px-4 py-3 bg-gradient-to-r from-[#00e5ff] to-[#0066ff] rounded-xl text-white font-rajdhani font-bold disabled:opacity-50 hover:shadow-lg hover:shadow-[#00e5ff]/20 transition"
                      >
                        {verifying ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Verify Team'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-4 border border-green-500/30">
                      <h3 className="text-white font-rajdhani font-bold text-lg">{verifiedTeam.teamName}</h3>
                      <p className="text-gray-400 text-sm">ID: {verifiedTeam.teamId}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm">
                        <p className="text-gray-400">Players: <span className="text-white">{Object.keys(verifiedTeam.players || {}).length}/5</span></p>
                        <p className="text-gray-400">Status: <span className={verifiedTeam.status === 'complete' ? 'text-green-400' : 'text-yellow-400'}>
                          {verifiedTeam.status === 'complete' ? 'COMPLETE' : 'INCOMPLETE'}
                        </span></p>
                      </div>
                    </div>

                    <div>
                      <label className="block text-gray-300 text-sm font-rajdhani font-bold mb-2">In-Game Name (IGN)</label>
                      <input
                        type="text"
                        value={joinIGN}
                        onChange={(e) => setJoinIGN(e.target.value)}
                        disabled={!!(joinGame === 'BGMI' ? profile?.BGMIIGN : profile?.FFMAXIGN)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#00e5ff]/50 transition disabled:opacity-50"
                        placeholder="Enter your IGN"
                      />
                      {!!(joinGame === 'BGMI' ? profile?.BGMIIGN : profile?.FFMAXIGN) && (
                        <p className="text-xs text-green-400 mt-1">Using your saved IGN</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-gray-300 text-sm font-rajdhani font-bold mb-2">In-Game ID</label>
                      <input
                        type="text"
                        value={joinID}
                        onChange={(e) => setJoinID(e.target.value)}
                        disabled={!!(joinGame === 'BGMI' ? profile?.BGMIID : profile?.FFMAXID)}
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#00e5ff]/50 transition disabled:opacity-50"
                        placeholder="Enter your ID"
                      />
                      {!!(joinGame === 'BGMI' ? profile?.BGMIID : profile?.FFMAXID) && (
                        <p className="text-xs text-green-400 mt-1">Using your saved ID</p>
                      )}
                    </div>

                    <div className="flex gap-3 pt-2">
                      <button
                        onClick={() => {
                          setVerifiedTeam(null);
                          resetJoinForm();
                        }}
                        className="flex-1 px-4 py-3 bg-white/5 rounded-xl text-gray-400 font-rajdhani font-bold hover:bg-white/10 transition"
                      >
                        Back
                      </button>
                      <button
                        onClick={handleJoinTeam}
                        disabled={joining || !joinIGN || !joinID}
                        className="flex-1 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl text-white font-rajdhani font-bold disabled:opacity-50 hover:shadow-lg hover:shadow-green-500/20 transition"
                      >
                        {joining ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'Join Team'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyTeam;